/**
 * TemplateCategoryPicker
 *
 * Category-specific template picker that shows the resolved default template
 * at the top with a "Use Default" quick-select, then lists other templates
 * in the same category below a divider.
 */

import { CheckCircleIcon, StarIcon } from '@heroicons/react/20/solid';
import { useCallback, useMemo } from 'react';

import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import type {
  ReadTemplateSummary,
  TemplateResolveApiArg,
} from '@/services/api/generated/templatesV2Service';

import {
  TEMPLATE_CATEGORY_LABELS,
  TEMPLATE_FORMAT_COLORS,
  TEMPLATE_SCOPE_COLORS,
} from '../constants';
import { TemplateSearchPicker } from './TemplateSearchPicker';
import { useResolvedTemplate } from './useResolvedTemplate';

interface TemplateCategoryPickerProps {
  /** The template category to pick from */
  category: TemplateResolveApiArg['category'];
  /** Firm ID for resolution and filtering */
  firmId?: string;
  /** Currently selected template ID */
  selectedId?: string | null;
  /** Called when a template is selected */
  onSelect: (template: ReadTemplateSummary) => void;
  /** Optional className */
  className?: string;
  /** Whether the picker is disabled */
  disabled?: boolean;
}

export const TemplateCategoryPicker = ({
  category,
  firmId,
  selectedId,
  onSelect,
  className,
  disabled = false,
}: TemplateCategoryPickerProps) => {
  const {
    template: defaultTemplate,
    sourceTier,
    isLoading: isResolving,
  } = useResolvedTemplate({ category, firmId });

  const categoryLabel =
    TEMPLATE_CATEGORY_LABELS[category] ?? category;

  const isDefaultExternal = defaultTemplate?.format === 'EXTERNAL';

  const handleUseDefault = useCallback(() => {
    if (defaultTemplate && !disabled && !isDefaultExternal) {
      // Map ReadTemplate to ReadTemplateSummary shape for the callback
      onSelect({
        id: defaultTemplate.id,
        name: defaultTemplate.name,
        display_name: defaultTemplate.display_name,
        format: defaultTemplate.format,
        category: defaultTemplate.category,
        scope: defaultTemplate.scope,
        firm_id: defaultTemplate.firm_id,
        status: defaultTemplate.status,
        is_default: defaultTemplate.is_default,
        version: defaultTemplate.version,
        version_group: defaultTemplate.version_group,
        created_at: defaultTemplate.created_at,
        modified_at: defaultTemplate.modified_at,
      });
    }
  }, [defaultTemplate, onSelect, disabled, isDefaultExternal]);

  const isDefaultSelected = useMemo(
    () =>
      defaultTemplate
        ? selectedId === (defaultTemplate.id as string)
        : false,
    [defaultTemplate, selectedId],
  );

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      {/* Header */}
      <div className="flex items-center gap-2">
        <h3 className="text-sm font-semibold text-zinc-800 dark:text-zinc-200">
          {categoryLabel}
        </h3>
      </div>

      {/* Default template section */}
      {isResolving ? (
        <DefaultTemplateSkeleton />
      ) : defaultTemplate ? (
        <div
          className={cn(
            'rounded-lg border p-3 transition-colors',
            isDefaultSelected
              ? 'border-indigo-300 bg-indigo-50/50 dark:border-indigo-500/40 dark:bg-indigo-500/10'
              : 'border-zinc-200 bg-zinc-50/50 dark:border-zinc-700 dark:bg-zinc-800/50',
          )}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 mb-1">
                <StarIcon className="h-4 w-4 text-amber-500 flex-shrink-0" />
                <span className="text-xs font-semibold text-zinc-500 uppercase">
                  Default
                </span>
                {sourceTier && (
                  <span className="text-[10px] font-medium text-zinc-500">
                    ({sourceTier})
                  </span>
                )}
              </div>
              <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100 truncate">
                {defaultTemplate.display_name || defaultTemplate.name}
              </p>
              <div className="flex items-center gap-1.5 mt-1">
                <span
                  className={cn(
                    'inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase',
                    TEMPLATE_FORMAT_COLORS[defaultTemplate.format] ??
                      'bg-zinc-100 text-zinc-600',
                  )}
                >
                  {defaultTemplate.format}
                </span>
                <span
                  className={cn(
                    'inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold',
                    TEMPLATE_SCOPE_COLORS[defaultTemplate.scope] ??
                      'bg-zinc-100 text-zinc-600',
                  )}
                >
                  {defaultTemplate.scope === 'GLOBAL' ? 'Global' : 'Firm'}
                </span>
              </div>
            </div>

            {isDefaultSelected ? (
              <span className="flex items-center gap-1 text-xs font-medium text-indigo-600 dark:text-indigo-400">
                <CheckCircleIcon className="h-4 w-4" />
                Selected
              </span>
            ) : (
              <button
                type="button"
                onClick={handleUseDefault}
                disabled={disabled || isDefaultExternal}
                title={isDefaultExternal ? 'External templates cannot be used for document generation' : undefined}
                className={cn(
                  'rounded-md px-3 py-1.5 text-xs font-semibold transition-colors',
                  'bg-indigo-600 text-white hover:bg-indigo-700',
                  'disabled:opacity-50 disabled:pointer-events-none',
                  'dark:bg-indigo-500 dark:hover:bg-indigo-600',
                )}
              >
                {isDefaultExternal ? 'External' : 'Use Default'}
              </button>
            )}
          </div>
        </div>
      ) : null}

      {/* Divider */}
      {defaultTemplate && (
        <div className="flex items-center gap-2">
          <div className="flex-1 border-t border-zinc-200 dark:border-zinc-700" />
          <span className="text-[10px] font-semibold text-zinc-500 uppercase">
            Or choose another
          </span>
          <div className="flex-1 border-t border-zinc-200 dark:border-zinc-700" />
        </div>
      )}

      {/* Full search picker for the category */}
      <TemplateSearchPicker
        selectedId={selectedId}
        onSelect={onSelect}
        searchOptions={{ category, firmId }}
        searchPlaceholder={`Search ${categoryLabel.toLowerCase()} templates...`}
        disabled={disabled}
      />
    </div>
  );
};

const DefaultTemplateSkeleton = () => (
  <div className="rounded-lg border border-zinc-200 dark:border-zinc-700 p-3">
    <div className="flex items-start justify-between gap-3">
      <div className="flex-1 space-y-2">
        <Skeleton className="h-3 w-16" />
        <Skeleton className="h-4 w-48" />
        <div className="flex gap-1.5">
          <Skeleton className="h-4 w-10 rounded" />
          <Skeleton className="h-4 w-10 rounded" />
        </div>
      </div>
      <Skeleton className="h-8 w-24 rounded-md" />
    </div>
  </div>
);
